'use client';

import { useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const LOCALES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'عربي' },
] as const;

/**
 * Locale dropdown — the trigger is a quiet text link (no button chrome).
 * Opens on hover (with a short close delay so the pointer can travel into
 * the menu) and on click/tap for touch + a11y. Lives in the navbar top row
 * on every breakpoint.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const locale = useLocale();
  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('header.menu');
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function switchLocale(next: string) {
    if (next === locale) return;
    const segments = pathname.split('/');
    segments[1] = next;
    router.push(segments.join('/'));
  }

  function cancelClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }

  function scheduleClose() {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-auto gap-1 bg-transparent px-0 py-0 text-small text-muted-foreground hover:bg-transparent hover:text-foreground',
            className,
          )}
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          aria-label={t('language')}
        >
          <span lang={locale}>{current.short}</span>
          <ChevronDown
            className={cn('size-3.5 transition-brand', open && 'rotate-180')}
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
        {LOCALES.map(({ code, label }) => (
          <DropdownMenuItem
            key={code}
            lang={code}
            onSelect={() => switchLocale(code)}
            className={cn(
              // Explicit :hover so the highlight is reliable — Radix only sets
              // data-[highlighted] on pointer-move, which misses the case where
              // the menu opens under an already-stationary cursor.
              'cursor-pointer hover:bg-accent hover:text-accent-foreground',
              code === locale && 'font-medium text-primary',
            )}
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Mobile language selector — a full-width segmented control sized for touch.
 * Lives only inside the full-screen mobile menu; desktop keeps the compact
 * `LanguageSwitcher` dropdown above. Uses shadcn Tabs instead of Button because
 * a segmented choice should not inherit the brand CTA hover gesture.
 */
export function MobileLanguageSwitcher({ className }: { className?: string }) {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const tMenu = useTranslations('header.menu');

  function switchLocale(next: string) {
    if (next === locale) return;
    const segments = pathname.split('/');
    segments[1] = next;
    router.push(segments.join('/'));
  }

  return (
    <Tabs value={locale} onValueChange={switchLocale} className={cn('w-full', className)}>
      <TabsList
        aria-label={tMenu('language')}
        className="h-auto w-full rounded-full border border-border bg-card p-1 text-small"
      >
        {LOCALES.map(({ code, label }) => (
          <TabsTrigger
            key={code}
            value={code}
            lang={code}
            className="h-auto rounded-full px-4 py-2.5 text-muted-foreground data-[state=active]:bg-primary data-[state=active]:font-medium data-[state=active]:text-primary-foreground"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
